import { html, TemplateResult } from 'lit';
import { Messages, Store } from '../../../@types/online-reservation.js';
import '../reservation-badges/reservation-badges.js';
import { Step } from './step.js';

export class ORStore extends Step {
  constructor(
    private readonly messages: Messages,
    private readonly onNext: () => void,
    private readonly selectStore: (store: Store) => void,
    private readonly size = '',
    private readonly showSizeBadge = true,
    private stores: Store[] = [],
    private readonly nearestStoreIds: number[] = [],
    private readonly onSizeBadgeClick?: () => void,
  ) {
    super();
  }

  renderStep(): TemplateResult {
    this.stores =
      this.nearestStoreIds.length > 0
        ? this.sortStoresByRanking(this.nearestStoreIds, this.stores)
        : this.sortStoresById(this.loadSelectedStoreId(), this.stores);
    return super.renderStep();
  }

  protected renderContent(): TemplateResult {
    return html`
      <reservation-badges
        .sizeDescription=${this.size}
        .showSizeBadge=${this.showSizeBadge}
        .onSizeBadgeClick=${this.onSizeBadgeClick}
      ></reservation-badges>
      <div class="ents-or-storedescription">
        <span class="ents-copy">${this.messages['stepThreeDescription']}</span>
      </div>
      ${this.stores.map((store: Store) => this.renderStoreItem(store))}
    `;
  }

  // eslint-disable-next-line class-methods-use-this
  protected renderFooter(): TemplateResult {
    return html``;
  }

  protected renderTitle(): TemplateResult {
    return html`
      <h2 id="or-step-title" class="ents-heading-medium" data-test-ents-or-stepthree-header tabindex="-1 ">
        ${this.messages['stepThreeHeader']}
      </h2>
    `;
  }

  protected renderStoreItem(store: Store) {
    return html`
      <label class="ents-copy-small-bold ents-or-item" data-test-ents-or-store-item>
        <input
          type="checkbox"
          @click="${this.createStoreSelectHandler(store)}"
          @keydown.enter="${this.createStoreSelectHandler(store)}"
          value="${store.name}"
          data-test-ents-or-next-button="${store.name}"
        />
        <div class="ents-or-options">
          <div class="ents-or-options-information" data-test-ents-or-size-item-text>
            <span>${store.name}</span>
          </div>
        </div>
      </label>
    `;
  }

  protected createStoreSelectHandler(store: Store) {
    return () => {
      this.saveSelectedStoreId(store.storeId);
      this.selectStore(store);
      this.onNext();
    };
  }

  // eslint-disable-next-line class-methods-use-this
  protected saveSelectedStoreId(selectedStoreId: number) {
    window.localStorage.setItem('ents:store-number', `${selectedStoreId}`);
  }

  // eslint-disable-next-line class-methods-use-this
  protected loadSelectedStoreId() {
    const id = parseInt(window.localStorage.getItem('ents:store-number') ?? '0', 10);
    return Number.isNaN(id) ? 0 : id;
  }

  // eslint-disable-next-line class-methods-use-this
  protected sortStoresById(storeId: number, stores: Store[]) {
    const sortedStores = [...stores];
    const index = sortedStores.findIndex((store) => store.storeId === storeId);
    if (index > -1) {
      const item = sortedStores.splice(index, 1)[0];
      sortedStores.unshift(item);
    }
    return sortedStores;
  }

  // eslint-disable-next-line class-methods-use-this
  protected sortStoresByRanking(nearestStoreIds: number[], stores: Store[]) {
    const rank = (store: Store) => {
      const index = nearestStoreIds.indexOf(store.storeId);
      return index === -1 ? nearestStoreIds.length : index;
    };
    return [...stores].sort((first, second) => rank(first) - rank(second));
  }
}
